import React from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SubscriptionStatus } from '@/hooks/useSubscription';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface UsageWarningProps {
  subscription: SubscriptionStatus | null;
  className?: string;
}

const UsageWarning: React.FC<UsageWarningProps> = ({ subscription, className = '' }) => {
  const navigate = useNavigate();

  if (!subscription) return null;

  const usagePercentage = (subscription.current_usage / subscription.monthly_limit) * 100;
  
  // Don't show warning if usage is below 80%
  if (usagePercentage < 80) return null;

  const isAtLimit = subscription.current_usage >= subscription.monthly_limit;
  const isNearLimit = usagePercentage >= 90;

  return (
    <div 
      className={`p-4 rounded-lg flex items-start gap-3 ${className}`}
      style={{ 
        background: isAtLimit 
          ? `${PALETTE.orange}15` 
          : `${PALETTE.orange}10`,
        border: `1px solid ${isAtLimit ? PALETTE.orange : `${PALETTE.orange}40`}`
      }}
    >
      {isAtLimit ? (
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
      ) : (
        <TrendingUp className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
      )}
      
      <div className="flex-1">
        <h4 className="font-semibold text-sm mb-1" style={{ color: PALETTE.gunmetal }}>
          {isAtLimit 
            ? 'Video Analysis Limit Reached' 
            : `${usagePercentage.toFixed(0)}% of Monthly Limit Used`
          }
        </h4>
        <p className="text-xs mb-3" style={{ color: `${PALETTE.gunmetal}80` }}>
          {isAtLimit 
            ? `You've used all ${subscription.monthly_limit} video analyses this month. New submissions are paused until your next billing cycle.`
            : `You've used ${subscription.current_usage} of ${subscription.monthly_limit} video analyses. Consider upgrading if you need more capacity.`
          }
        </p>
        
        <div className="flex items-center gap-2">
          {/* Progress Bar */}
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: `${PALETTE.americanSilver}40` }}>
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(100, usagePercentage)}%`,
                background: isAtLimit ? PALETTE.orange : isNearLimit ? PALETTE.orange : PALETTE.inchworm
              }}
            />
          </div>
          <span className="text-xs font-medium" style={{ color: PALETTE.gunmetal }}>
            {subscription.remaining} left
          </span>
        </div>

        {!subscription.has_subscription && (
          <Button
            onClick={() => navigate('/subscription')}
            size="sm"
            className="mt-3 rounded-full"
            style={{ 
              background: PALETTE.gunmetal, 
              color: PALETTE.brightSnow 
            }}
          >
            Upgrade Now
          </Button>
        )}
      </div>
    </div>
  );
};

export default UsageWarning;

